import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { Page } from '../types';
import { collection, doc, limit, onSnapshot, orderBy, query, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { getFirebaseFunctions, getFirestoreDB } from '../lib/firebase';
import BackToPosButton from '../components/common/BackToPosButton';

interface ChatThreadRow {
  id: string;
  customerName?: string;
  customerEmail?: string | null;
  lastMessageText?: string;
  lastMessageAt?: number;
  lastFrom?: 'customer' | 'staff';
  unreadByStaff?: number;
}

interface ChatMessageRow {
  id: string;
  from: 'customer' | 'staff';
  staffName?: string | null;
  text: string;
  orderLabel?: string | null;
  createdAt: number;
  deliveredToApp?: boolean;
}

interface CustomerAppChatPageProps {
  onNavigate?: (page: Page) => void;
}

const QUICK_REPLIES = [
  'Thanks — noted on your kitchen ticket.',
  'Your order is being prepared now.',
  'Your order is ready for pickup at the counter.',
];

function formatTime(ms?: number): string {
  if (!ms) return '';
  const d = new Date(ms);
  const sameDay = d.toDateString() === new Date().toDateString();
  return sameDay
    ? d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : d.toLocaleDateString([], { day: 'numeric', month: 'short' }) + ' ' + d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

/**
 * Kitchen & staff chat inbox for the customer app. Threads/messages are mirrored into
 * `app_chat_threads` by the app's `sendChatMessage`; replies go through `replyCustomerAppChat`,
 * which forwards them to the customer (live in-app + push).
 */
const CustomerAppChatPage: React.FC<CustomerAppChatPageProps> = ({ onNavigate }) => {
  const [threads, setThreads] = useState<ChatThreadRow[]>([]);
  const [threadsError, setThreadsError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessageRow[]>([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const q = query(collection(getFirestoreDB(), 'app_chat_threads'), orderBy('lastMessageAt', 'desc'), limit(100));
    return onSnapshot(
      q,
      (snap) => {
        setThreadsError(null);
        setThreads(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<ChatThreadRow, 'id'>) })));
      },
      (err) => setThreadsError(err.message || 'Could not load chats.'),
    );
  }, []);

  useEffect(() => {
    if (!selectedId) {
      setMessages([]);
      return undefined;
    }
    const q = query(
      collection(getFirestoreDB(), 'app_chat_threads', selectedId, 'messages'),
      orderBy('createdAt', 'asc'),
      limit(200),
    );
    return onSnapshot(q, (snap) => {
      setMessages(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<ChatMessageRow, 'id'>) })));
    });
  }, [selectedId]);

  const selected = useMemo(() => threads.find((t) => t.id === selectedId) ?? null, [threads, selectedId]);

  // Opening a thread clears its unread badge.
  useEffect(() => {
    if (!selected || !selected.unreadByStaff) return;
    updateDoc(doc(getFirestoreDB(), 'app_chat_threads', selected.id), { unreadByStaff: 0 }).catch(() => {
      /* badge only */
    });
  }, [selected]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [messages.length]);

  const send = async (text: string) => {
    const trimmed = text.trim();
    if (!selectedId || !trimmed || sending) return;
    setSending(true);
    setSendError(null);
    try {
      await httpsCallable(getFirebaseFunctions(), 'replyCustomerAppChat')({ userId: selectedId, text: trimmed });
      setDraft('');
    } catch (err) {
      setSendError(err instanceof Error ? err.message : 'Reply failed.');
    } finally {
      setSending(false);
    }
  };

  const totalUnread = threads.reduce((sum, t) => sum + (t.unreadByStaff || 0), 0);

  return (
    <div className="flex flex-col h-full">
      <header className="bg-white shadow-md px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            Customer app chat
            {totalUnread > 0 && (
              <span className="ml-2 align-middle text-sm bg-pink-500 text-white rounded-full px-2 py-0.5">{totalUnread}</span>
            )}
          </h1>
          <p className="text-sm text-gray-500">Messages from the app's “Kitchen &amp; staff” chat. Replies reach the customer instantly.</p>
        </div>
        <div className="flex items-center gap-3">
          {onNavigate && (
            <button type="button" className="text-sm text-pink-600" onClick={() => onNavigate('loyalty')}>
              ← Loyalty &amp; offers
            </button>
          )}
          <BackToPosButton />
        </div>
      </header>

      <main className="flex-1 min-h-0 flex">
        <aside className="w-80 shrink-0 border-r bg-white overflow-y-auto">
          {threadsError && <p className="m-3 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3">{threadsError}</p>}
          {threads.length === 0 && !threadsError && <p className="p-4 text-sm text-gray-500">No conversations yet.</p>}
          {threads.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setSelectedId(t.id)}
              className={`w-full text-left px-4 py-3 border-b hover:bg-gray-50 ${t.id === selectedId ? 'bg-pink-50' : ''}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold text-gray-900 truncate">{t.customerName || 'Customer'}</span>
                <span className="text-xs text-gray-500 shrink-0">{formatTime(t.lastMessageAt)}</span>
              </div>
              <div className="flex items-center justify-between gap-2 mt-0.5">
                <span className={`text-sm truncate ${t.unreadByStaff ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
                  {t.lastFrom === 'staff' ? 'You: ' : ''}
                  {t.lastMessageText}
                </span>
                {!!t.unreadByStaff && (
                  <span className="text-xs bg-pink-500 text-white rounded-full px-2 py-0.5 shrink-0">{t.unreadByStaff}</span>
                )}
              </div>
            </button>
          ))}
        </aside>

        <section className="flex-1 min-w-0 flex flex-col bg-gray-50">
          {!selected ? (
            <div className="flex-1 flex items-center justify-center text-gray-500 text-sm">Select a conversation</div>
          ) : (
            <>
              <div className="px-5 py-3 bg-white border-b">
                <p className="font-semibold text-gray-900">{selected.customerName || 'Customer'}</p>
                {selected.customerEmail && <p className="text-xs text-gray-500">{selected.customerEmail}</p>}
              </div>
              <div className="flex-1 overflow-y-auto p-5 space-y-3">
                {messages.map((m) => (
                  <div key={m.id} className={`flex ${m.from === 'staff' ? 'justify-end' : 'justify-start'}`}>
                    <div
                      className={`max-w-[70%] rounded-2xl px-4 py-2 text-sm ${m.from === 'staff' ? 'bg-pink-500 text-white' : 'bg-white border text-gray-900'}`}
                    >
                      {m.orderLabel && (
                        <p className={`text-xs mb-1 ${m.from === 'staff' ? 'text-pink-100' : 'text-gray-500'}`}>About order #{m.orderLabel}</p>
                      )}
                      <p className="whitespace-pre-wrap">{m.text}</p>
                      <p className={`text-[11px] mt-1 ${m.from === 'staff' ? 'text-pink-100' : 'text-gray-400'}`}>
                        {m.from === 'staff' && m.staffName ? `${m.staffName} · ` : ''}
                        {formatTime(m.createdAt)}
                        {m.from === 'staff' && m.deliveredToApp === false ? ' · not delivered' : ''}
                      </p>
                    </div>
                  </div>
                ))}
                <div ref={bottomRef} />
              </div>
              <div className="bg-white border-t p-3 space-y-2">
                <div className="flex flex-wrap gap-2">
                  {QUICK_REPLIES.map((q) => (
                    <button
                      key={q}
                      type="button"
                      className="text-xs px-3 py-1 rounded-full border border-gray-300 text-gray-700 hover:bg-gray-50"
                      onClick={() => setDraft(q)}
                    >
                      {q}
                    </button>
                  ))}
                </div>
                {sendError && <p className="text-sm text-red-600">{sendError}</p>}
                <form
                  className="flex gap-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void send(draft);
                  }}
                >
                  <input
                    className="flex-1 border rounded-md px-3 py-2 text-sm"
                    placeholder="Reply to customer…"
                    maxLength={1000}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                  />
                  <button
                    type="submit"
                    disabled={sending || !draft.trim()}
                    className="px-4 py-2 bg-pink-500 text-white rounded-md text-sm disabled:opacity-50"
                  >
                    {sending ? 'Sending…' : 'Send'}
                  </button>
                </form>
              </div>
            </>
          )}
        </section>
      </main>
    </div>
  );
};

export default CustomerAppChatPage;
